import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  DEMO_DEFAULTS,
  DEMO_OFF,
  DemoContext,
  type DataState,
  type DemoSettings,
  type DemoValue,
} from '@/lib/demo/demo-context'

const STORAGE_KEY = 'ringout-console-demo'

const DATA_STATES: readonly DataState[] = ['populated', 'empty', 'loading', 'partial', 'error', 'offline']

function readStored(): DemoSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<DemoSettings> | null
    if (!raw || typeof raw !== 'object') return DEMO_DEFAULTS
    return {
      enabled: raw.enabled === true,
      dataState: DATA_STATES.includes(raw.dataState as DataState) ? (raw.dataState as DataState) : 'populated',
      role: raw.role === 'admin' || raw.role === 'receptionist' ? raw.role : 'owner',
      overviewLayout: raw.overviewLayout === 'B' || raw.overviewLayout === 'C' ? raw.overviewLayout : 'A',
      agentPaused: raw.agentPaused === true,
    }
  } catch {
    return DEMO_DEFAULTS
  }
}

function DevDemoProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<DemoSettings>(readStored)

  const update = useCallback((patch: Partial<DemoSettings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // Private mode: the settings last for this page only.
      }
      return next
    })
  }, [])

  const value = useMemo<DemoValue>(
    () => ({
      ...settings,
      setEnabled: (enabled) => update({ enabled }),
      setDataState: (dataState) => update({ dataState }),
      setRole: (role) => update({ role }),
      setOverviewLayout: (overviewLayout) => update({ overviewLayout }),
      setAgentPaused: (agentPaused) => update({ agentPaused }),
    }),
    [settings, update],
  )

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>
}

/**
 * Holds the prototype controls' settings for the console. In a production build
 * it provides the switched-off value and nothing else: `import.meta.env.DEV` is
 * replaced with `false`, so the dev branch and its storage are dropped.
 */
export function DemoProvider({ children }: { children: ReactNode }) {
  if (import.meta.env.DEV) return <DevDemoProvider>{children}</DevDemoProvider>
  return <DemoContext.Provider value={DEMO_OFF}>{children}</DemoContext.Provider>
}
